import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useUser } from '../../../../../context/UserContext';
import { useDispatch, useSelector } from 'react-redux';
import { saveAs } from 'file-saver';
import { v4 as uuidv4 } from 'uuid';
import '../DCOEstimator.css';

// Utils
import { getConfig } from '../../../../../utils/configStorage';
import { salvarAuto } from '../../../../../utils/propostas';
import { gerarPDF } from '../utils/pdfGenerator';
import { formatarHorasDecimalParaHHMMSS } from '../utils/format';
import { calcularPeso, validateForm } from '../utils/calculations';

// Redux Actions
import { fetchConfig } from '../../../../../store/actions/configActions';

// Constantes
import {
    dimensoesOptions,
    tipoDcoOptions,
    formatoOptions,
    stepsOptions,
    mockupOptions,
    alimentacaoOptions,
    urgenciaOptions,
    volumetriaOptions,
    pesos
} from '../constants/constants';

// Componentes
import FieldWithTooltipComponent from '../../../../../components/Tooltip/FieldWithTooltip';
import InputComponent from '../../../../../components/general/Input/Input';
import SelectComponent from '../../../../../components/general/Input/Select/Select';
import MultiCheckboxSelectComponent from '../../../../../components/general/Input/MultiCheckboxSelect/MultiCheckboxSelect';
import ButtonComponent from '../../../../../components/general/Button/Button';
import TitlePageComponent from '../../../../../components/general/TitlePage/TitlePage';
import TitleSectionComponent from '../../../../../components/intern/TitleSectionComponent/TitleSectionComponent';
import ConfirmationPopupComponent from '../../../../../components/intern/ConfirmationPopup/ConfirmationPopup';
import AlertComponent from '../../../../../components/intern/Alert/Alert';
import AccordionItemComponent from '../../../../../components/general/AccordionItem/AccordionItem';
import RadioGroupComponent from '../../../../../components/RadioGroup/RadioGroup';
import TextareaComponent from '../../../../../components/general/Textarea/Textarea';
import EditableFieldComponent from '../../../../../components/EditableField/EditableField';

// Assets
import cabecalhoImg from '../../../../../assets/logo_innovid.png';
import rodapeImg from '../../../../../assets/logo_adsmovil.png';

/* ======================================================
   CONSTANTES & HELPERS
====================================================== */
const formatCurrency = (value, currency = 'USD') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value || 0);

const formatHours = (value) =>
    formatarHorasDecimalParaHHMMSS(Number(value) || 0);

const NOVA_ESTIMATIVA = (id) => ({
    id,
    formato: '',
    steps: '',
    versoes: 0,
    mockup: '',
    desdobramentos: 0,
    carrossel: false,
    dvastSkip: false,
    alimentacao: '',
    volumetria: '',
    viewabilityAvancado: false,
    possuiItensDinamicos: false,
    descricaoItensDinamicos: '',
    dimensoesSelecionadas: [],
    outrasDimensoes: '',
    precoPorLinhaPowerfeeds: 0.01,
    tipoDCO: [],
});

const getAdaptacaoLabel = (mockup) => {
    switch (mockup) {
        case 'desdobrar': return 'Number of adaptations';
        case 'criar': return 'Number of dimensions to create';
        case 'clienteFornece': return 'Number of provided mockups';
        default: return null;
    }
};

const LIMITE_ALTERACOES = 3;
const NUMERIC_FIELDS = new Set(['versoes', 'desdobramentos', 'precoPorLinhaPowerfeeds']);
const BOOLEAN_FIELDS = new Set(['carrossel', 'dvastSkip', 'viewabilityAvancado', 'possuiItensDinamicos']);
const opcoesSimNao = [
    { value: true, label: "Yes" },
    { value: false, label: "No" }
];

/* ======================================================
   COMPONENTE
====================================================== */

const DCOEstimator = () => {
    const { authUser, userDb } = useUser();
    const dispatch = useDispatch();
    const { configStore } = useSelector((state) => state.configStore);
    const [pendingReason, setPendingReason] = useState(null);


    const emailsPermitidos = [
        "marcelo@adsmovil.com", "tamires@adsmovil.com", "ruzito@gmail.com", "raul@adsmovil.com"
    ];

    const usuarioAutorizado = emailsPermitidos.includes(authUser?.email);

    // Configurações do Redux
    const [config, setConfig] = useState({
        precoHora: 40,
        horasPorPeso: 2,
        cotacao: 6,
        moeda: 'USD',
        prioridadeBaixa: 0,
        prioridadeMedia: 10,
        prioridadeAlta: 20,
        precoPorLinhaPowerfeed: 0.01,
        links: {
            mockupSpecs: '',
            psdMockup: '',
            entregaMockup: '',
            planilhaVersoes: ''
        }
    });


    const [uiState, setUIState] = useState({
        acrescimoUrgencia: 0,
        highlightResultado: false,
        alert: { type: '', message: '' },
        showPopup: false,
        deleteIndex: null,
    });

    // Adicione estas funções auxiliares
    const setAlertComponent = (alert) => setUIState(p => ({ ...p, alert }));
    const showAlertComponent = (type, message) => setUIState(p => ({ ...p, alert: { type, message } }));
    const setShowPopup = (value) => setUIState(p => ({ ...p, showPopup: value }));

    // Dados principais
    const [configOriginal, setConfigOriginal] = useState(null);
    const [dadosAlterados, setDadosAlterados] = useState(false);
    const [errors, setErrors] = useState([]);
    const [errosComerciais, setErrosComerciais] = useState({});
    const [resultado, setResultado] = useState([]);
    const [estimativas, setEstimates] = useState([NOVA_ESTIMATIVA(1)]);
    const [dadosComerciais, setDadosComerciais] = useState({
        id: uuidv4(),
        nomeProjeto: '',
        nomeCliente: '',
        urgencia: '',
    });

    // Aprovação
    const [pendenteAprovacao, setPendenteAprovacao] = useState(false);
    const [pdfData, setPdfData] = useState(null);

    // Refs
    const resultadoRef = useRef(null);
    const nextId = useRef(2);

    // Desestruturações
    const { precoHora, moeda, prioridadeBaixa, prioridadeMedia, prioridadeAlta } = config;
    const { acrescimoUrgencia, highlightResultado, showPopup, alert, deleteIndex } = uiState;


    const [primeiroCalculo, setPrimeiroCalculo] = useState(true);
    const [alteracoes, setAlteracoes] = useState(0);


    /* ======================================================
       EFFECTS
    ====================================================== */
    useEffect(() => {
        dispatch(fetchConfig());
    }, [dispatch]);

    useEffect(() => {
        if (configStore && configStore.length > 0) {
            const firstConfig = configStore[0];
            const configInicial = {
                precoHora: firstConfig.precoHora || 40,
                horasPorPeso: firstConfig.horasPorPeso || 2,
                cotacao: firstConfig.cotacao || 6,
                moeda: firstConfig.moeda || 'USD',
                prioridadeBaixa: firstConfig.prioridadeBaixa || 0,
                prioridadeMedia: firstConfig.prioridadeMedia || 10,
                prioridadeAlta: firstConfig.prioridadeAlta || 20,
                precoPorLinhaPowerfeed: firstConfig.precoPorLinhaPowerfeed || 0.01,
                links: {
                    mockupSpecs: firstConfig.linkMockupSpecs || '',
                    psdMockup: firstConfig.linkPsdMockup || '',
                    entregaMockup: firstConfig.linkEntregaMockup || '',
                    planilhaVersoes: firstConfig.linkPlanilhaVersoes || ''
                }
            }
            setConfig(configInicial);
            setConfigOriginal(configInicial); // Guarda a versão original
            setDadosAlterados(false);
        }
    }, [configStore]);



    useEffect(() => {
        if (configOriginal && config) {
            const camposCriticos = [
                'precoHora',
                'horasPorPeso',
                'cotacao',
                'moeda',
                'precoPorLinhaPowerfeed'
            ];

            const alterado = camposCriticos.some(campo =>
                configOriginal[campo] !== config[campo]
            );

            setDadosAlterados(alterado);
        }
    }, [config, configOriginal]);


    // 📌 Regras comerciais por valor
    const criticalFields = ['precoHora', 'horasPorPeso', 'cotacao', 'moeda', 'precoPorLinhaPowerfeed'];

    const priceReduced = configOriginal && config
        ? criticalFields.some(campo =>
            Number(config[campo]) < Number(configOriginal[campo])
        )
        : false;

    const priceIncreased = configOriginal && config
        ? criticalFields.some(campo =>
            Number(config[campo]) > Number(configOriginal[campo])
        )
        : false;


    /*useEffect(() => {
        if (userDb) {
            setDadosComerciais((prev) => ({
                ...prev,
                responsavel: `${userDb.userName || ''} ${userDb.userLastname || ''}`.trim(),
            }));
        }
        const storedConfig = getConfig();
        if (storedConfig) {
            setConfig(prev => ({
                ...prev,
                ...storedConfig,
                links: { ...prev.links, ...(storedConfig.links || {}) }
            }));
        }
    }, []); */

    useEffect(() => {
        const storedConfig = getConfig();
        if (storedConfig) {
            setConfig(prev => ({
                ...prev,
                ...storedConfig,
                links: { ...prev.links, ...(storedConfig.links || {}) }
            }));
        }

        if (!userDb && !authUser) return;

        const nome =
            userDb?.fullName ||
            `${userDb?.firstname || ''} ${userDb?.lastname || ''}`.trim() ||
            userDb?.userName ||
            authUser?.displayName ||
            authUser?.email?.split('@')[0] ||
            '';

        setDadosComerciais(prev => ({
            ...prev,
            responsavel: nome
        }));
    }, [userDb, authUser]);


    useEffect(() => {
        if (!dadosComerciais.nomeProjeto?.trim()) return;
        salvarAuto({
            id: dadosComerciais.id,
            nomeProjeto: dadosComerciais.nomeProjeto,
            nomeCliente: dadosComerciais.nomeCliente,
            responsavel: dadosComerciais.responsavel,
            dadosComerciais,
            estimativas,
            resultado,
            version: 1,
        });
    }, [dadosComerciais, estimativas, resultado]);

    useEffect(() => {
        let perc = 0;
        switch (dadosComerciais.urgencia) {
            case 'baixa': perc = prioridadeBaixa; break;
            case 'media': perc = prioridadeMedia; break;
            case 'alta': perc = prioridadeAlta; break;
            default: perc = 0;
        }
        setUIState(p => ({ ...p, acrescimoUrgencia: perc }));
    }, [dadosComerciais.urgencia, prioridadeBaixa, prioridadeMedia, prioridadeAlta]);

    const [loading, setLoading] = useState(false);

    /* useEffect(() => {
         if (!dadosComerciais.nomeProjeto) return;
         if (!estimativas.some(e => e.formato)) return;
 
         setLoading(true);
         try {
             calcularEsforco();
         } finally {
             setTimeout(() => setLoading(false), 350);
         }
     }, [estimativas, config, dadosComerciais]); */


    useEffect(() => {
        if (primeiroCalculo) return;
        if (!dadosComerciais.nomeProjeto?.trim()) return;
        if (!estimativas.some(e => e.formato)) return;

        if (alteracoes < LIMITE_ALTERACOES) {
            setLoading(true);
            calcularEsforco();
            setTimeout(() => setLoading(false), 350);
        } else {
            setResultado([]);
        }
    }, [estimativas, config, dadosComerciais]);



    /* ======================================================
       FUNÇÕES PRINCIPAIS
    ====================================================== */
    const handleMultiChange = (index, name, rawValue) => {
        if (!primeiroCalculo && alteracoes >= LIMITE_ALTERACOES) {
            setResultado([]);
            return;
        }

        const updated = [...estimativas];
        let value = rawValue;

        if (BOOLEAN_FIELDS.has(name)) {
            value = rawValue === true || rawValue === 'true';
        } else if (NUMERIC_FIELDS.has(name)) {
            const n = Number(rawValue);
            value = Math.max(0, Number.isFinite(n) ? n : 0);
        }
        updated[index] = { ...updated[index], [name]: value };
        setEstimates(updated);

        if (!primeiroCalculo) {
            setAlteracoes(a => Math.min(LIMITE_ALTERACOES, a + 1));
        }
    };


    const solicitarReducaoPreco = async (novoValor) => {
        try {
            await fetch('/api/solicitar-aprovacao-preco', {
                method: 'POST',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    responsavel: dadosComerciais.responsavel,
                    nomeProjeto: dadosComerciais.nomeProjeto,
                    precoAtual: configOriginal?.precoHora,
                    precoSolicitado: novoValor
                }),
            });

            showAlertComponent(
                "envio",
                "Request sent to approver. The change will only be applied after approval."
            );

            setPendenteAprovacao(true);

        } catch (err) {
            console.error(err);
            showAlertComponent("erro", "Error requesting approval.");
        }
    };

    const calcularEsforco = () => {
        if (!validateForm(estimativas, dadosComerciais, setErrosComerciais, setErrors, setAlertComponent)) {
            return;
        }
        if (!config?.horasPorPeso || !config?.precoHora) {
            return setAlertComponent({ type: 'erro', message: 'The calculation configuration is incomplete or invalid.' });
        }

        const resultados = estimativas.map((est) => {
            const pesoTotal = calcularPeso({ ...est, urgencia: dadosComerciais.urgencia });
            const horas = (pesoTotal * config.horasPorPeso) + (est.desdobramentos * (pesos?.desdobramentos?.base || 0));
            const versoes = Math.max(est.versoes ?? 0, 0);

            const precoLinha = config.precoPorLinhaPowerfeed || 0.01;
            const precoBaseSemExtras = horas * config.precoHora;
            const custoPowerfeed = (est.alimentacao === 'powerfeed' && versoes > 0) ? precoLinha * versoes : 0;
            const precoBase = precoBaseSemExtras + custoPowerfeed;

            const acrescimoValor = precoBase * ((acrescimoUrgencia || 0) / 100);
            const precoFinal = precoBase + acrescimoValor;

            return {
                id: est.id,
                formato: est.formato,
                precoHora: config.precoHora,
                pesoTotal,
                precoBase,
                acrescimoUrgencia: acrescimoUrgencia || 0,
                acrescimoValor,
                custoPowerfeed,
                preco: precoFinal,
                moeda: config.moeda,
                versoes,
                precoPorLinhaPowerfeeds: precoLinha,
                horas,
            };
        });

        setResultado(resultados);
        setUIState(p => ({ ...p, highlightResultado: true, alert: { type: 'adicionar', message: 'Estimate calculated successfully.' } }));
        resultadoRef.current?.scrollIntoView({ behavior: 'smooth' });
        setTimeout(() => setUIState(p => ({ ...p, highlightResultado: false })), 2000);
    };

    const enviarParaAprovacao = async (pdfBlob) => {
        try {
            // Simular envio por email
            const formData = new FormData();
            formData.append('pdf', pdfBlob, `Proposal_${dadosComerciais.nomeProjeto}.pdf`);
            formData.append('recipients', 'marcelo@adsmovil.com,raul@adsmovil.com, tamires@adsmovil.com');
            formData.append('submittedBy', dadosComerciais.responsavel);
            formData.append('project', dadosComerciais.nomeProjeto);

            // Chamada para API de email (simulada)
            const response = await fetch('/api/enviar-email-aprovacao', {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                setAlertComponent({ type: 'adicionar', message: 'Proposal successfully submitted for approval!' });
                setPendenteAprovacao(true);
                setPdfData({
                    blob: pdfBlob,
                    nomeArquivo: `Proposta_${dadosComerciais.nomeProjeto}.pdf`,
                    submetidoPor: dadosComerciais.responsavel,
                    dataEnvio: new Date().toISOString()
                });
            } else {
                throw new Error('Failed to send for approval.');
            }
        } catch (error) {
            setAlertComponent({ type: 'erro', message: 'Error sending for approval. Please try again.' });
        }
    };

    const handleGerarPDF = async (modo = 'comercial') => {

        if (!validateForm(estimativas, dadosComerciais, setErrosComerciais, setErrors, setAlertComponent) || resultado.length === 0) return;

        try {
            const pdfBlob = await gerarPDF({
                estimativas,
                resultado,
                formatoOptions,
                stepsOptions,
                alimentacaoOptions,
                urgenciaOptions,
                mockupOptions,
                volumetriaOptions,
                getAdaptacaoLabel,
                showAlertComponent,
                cabecalhoImg,
                rodapeImg,
                moeda: config.moeda || 'USD',
                nomeProjeto: dadosComerciais.nomeProjeto,
                nomeCliente: dadosComerciais.nomeCliente,
                responsavel: dadosComerciais.responsavel,
                modo,
                links: config.links,
                prioridades: {
                    baixa: config.prioridadeBaixa,
                    media: config.prioridadeMedia,
                    alta: config.prioridadeAlta
                }
            });

            // Validação para evitar erro no saveAs
            if (!pdfBlob || !(pdfBlob instanceof Blob)) {
                setAlertComponent({ type: 'erro', message: 'Failed to generate PDF. Please try again later.' });
                return;
            }

            // Regra de aprovação consolidada
            if (usuarioAutorizado) {
                showAlertComponent(
                    "adicionar",
                    "Proposal sent successfully! Your profile is authorized and does not require approval."
                );
                setPendenteAprovacao(false);
            } else {
                if (priceReduced) {
                    showAlertComponent(
                        "info",
                        "Price reduction detected. Proposal sent to approval workflow."
                    );
                    setPendenteAprovacao(true);
                    setPendingReason(priceReduced ? "Price Reduction" : "PowerFeed Cost Change");

                    modo = 'aprovacao'; // força envio para aprovação
                } else {
                    showAlertComponent(
                        "success",
                        "Proposal sent successfully — approval not required."
                    );
                    setPendenteAprovacao(false);
                }
            }

            if (modo === 'aprovacao') {
                await enviarParaAprovacao(pdfBlob);

                setAlteracoes(0);
                setPrimeiroCalculo(true);

            } else {
                saveAs(pdfBlob, `Proposta_${dadosComerciais.nomeProjeto}.pdf`);

                setAlteracoes(0);
                setPrimeiroCalculo(true);
            }
        } catch (error) {
            console.error('Erro ao gerar PDF:', error);
            setAlertComponent({ type: 'erro', message: `Erro técnico: ${error.message}` });
        }

    };

    const handleRemoveClick = (index) => setUIState(p => ({ ...p, deleteIndex: index, showPopup: true }));
    const handleConfirmDelete = () => {
        setEstimates((prev) => prev.filter((_, i) => i !== deleteIndex));
        setShowPopup(false);
        setUIState(p => ({ ...p, deleteIndex: null }));
    };

    const gerarResumoEstimada = (est) => {
        const volumetriaLabel = volumetriaOptions.find(o => o.value === est.volumetria)?.label || '—';
        const mockupLabel = mockupOptions.find(o => o.value === est.mockup)?.label || '—';
        const formatoLabel = formatoOptions.find(o => o.value === est.formato)?.label || '—';
        const versoesLabel = est.versoes || '—';
        const urgenciaLabel = urgenciaOptions.find(o => o.value === dadosComerciais.urgencia)?.label || '—';
        const stepsLabel = stepsOptions.find(o => o.value === est.steps)?.label || '—';
        const alimentacaoLabel = alimentacaoOptions.find(o => o.value === est.alimentacao)?.label || '—';

        const tipoDcoLabels = Array.isArray(est.tipoDCO) && est.tipoDCO.length
            ? est.tipoDCO
                .map(v => tipoDcoOptions.find(o => o.value === v)?.label || v)
                .join(', ')
            : '—';

        const dimensoesCombinadas = [
            ...(est.dimensoesSelecionadas || []),
            est.outrasDimensoes
        ].filter(Boolean).join(', ') || '—';

        return `
Format: ${formatoLabel}
Steps: ${stepsLabel}
Versions: ${versoesLabel}
Dimensions: ${dimensoesCombinadas}
Mockup: ${mockupLabel}
Breakdowns: ${est.desdobramentos || '—'}
Volume: ${volumetriaLabel}
Priority: ${urgenciaLabel}
Customization Logic: ${tipoDcoLabels}
Feedback: ${alimentacaoLabel}
Dynamic Items: ${est.possuiItensDinamicos ? 'Yes' : 'No'}
Dynamic Items Description: ${est.descricaoItensDinamicos || '—'}
Advanced Viewability: ${est.viewabilityAvancado ? 'Yes' : 'No'}
Carosel (Shoppable): ${est.carrossel ? 'Yes' : 'No'}
DVAST with Skip: ${est.dvastSkip ? 'Yes' : 'No'}
`.trim();
    };

    const isLimparAtivo =
        estimativas.some((est) =>
            est.formato || est.steps || est.mockup || est.alimentacao || est.volumetria ||
            est.versoes > 0 || est.desdobramentos > 0 || est.carrossel || est.dvastSkip ||
            est.viewabilityAvancado || est.possuiItensDinamicos || est.descricaoItensDinamicos ||
            (est.dimensoesSelecionadas && est.dimensoesSelecionadas.length) || est.outrasDimensoes
        ) ||
        dadosComerciais.nomeProjeto || dadosComerciais.nomeCliente ||
        dadosComerciais.responsavel || dadosComerciais.urgencia;

    return (
        <section className="container-extern content-with-sidebar">
            {alert.type && (
                <AlertComponent
                    type={alert.type}
                    message={alert.message}
                    onClose={() => setUIState(p => ({ ...p, alert: { type: '', message: '' } }))}
                />
            )}


            <TitlePageComponent
                title="Commercial Proposal for Dynamic Creatives"
                subtitle="Create customized commercial proposals and generate a consolidated report."
            />


            <Helmet>
                <title>Commercial Proposal</title>
            </Helmet>

            <TitleSectionComponent>Project Information</TitleSectionComponent>

            <div className="input-row">
                <FieldWithTooltipComponent label="Project Name" tooltip="Give a unique name to quickly identify this proposal in the future.">
                    <InputComponent
                        type="text"
                        className={errosComerciais.nomeProjeto ? 'error' : ''}
                        value={dadosComerciais.nomeProjeto}
                        onChange={(e) => setDadosComerciais({ ...dadosComerciais, nomeProjeto: e.target.value })}
                        errorMessage={errosComerciais.nomeProjeto && "Please provide the project name."}
                    />
                </FieldWithTooltipComponent>

                <FieldWithTooltipComponent label="Client Name" tooltip="Enter the company or brand name to personalize your proposal.">
                    <InputComponent
                        type="text"
                        className={errosComerciais.nomeCliente ? 'error' : ''}
                        value={dadosComerciais.nomeCliente}
                        onChange={(e) => setDadosComerciais({ ...dadosComerciais, nomeCliente: e.target.value })}
                        errorMessage={errosComerciais.nomeCliente && "Please provide the client name."}
                    />
                </FieldWithTooltipComponent>

                <FieldWithTooltipComponent label="Proposal Owner" tooltip="Name of the main contact responsible for this proposal.">
                    <InputComponent
                        type="text"
                        className=""
                        value={dadosComerciais.responsavel} // pré-preenchido
                        disabled={true}                     // não editável
                    />
                </FieldWithTooltipComponent>

                <FieldWithTooltipComponent label="Priority" tooltip="Sets the project priority. May affect the total cost.">
                    <SelectComponent
                        value={dadosComerciais.urgencia}
                        onChange={(e) => setDadosComerciais({ ...dadosComerciais, urgencia: e.target.value })}
                        options={urgenciaOptions}
                        error={errosComerciais.urgencia}
                        errorMessage={errosComerciais.urgencia && "Please select the priority."}
                    />

                </FieldWithTooltipComponent>

                {['media', 'alta'].includes(dadosComerciais.urgencia) && (
                    <FieldWithTooltipComponent
                        label="Applied Increase (%)"
                        tooltip="Click to edit the percentage increase to be added to the final value."
                    >
                        <EditableFieldComponent
                            value={acrescimoUrgencia}
                            onChange={(val) => setUIState((p) => ({ ...p, acrescimoUrgencia: Number(val) || 0 }))}
                            suffix="%"
                            error={errosComerciais.acrescimoUrgencia}
                        />
                    </FieldWithTooltipComponent>
                )}

                <FieldWithTooltipComponent
                    label={`Hourly Rate (${config.moeda || 'USD'})`}
                    tooltip="Click the icon to edit the value."
                >
                    {/*<EditableFieldComponent
                        value={precoHora}
                        onChange={(val) => setConfig(p => ({ ...p, precoHora: Number(val) || 0 }))}
                        currency={config.moeda || 'USD'}
                        locale="en-US"
                    /> */}

                    <EditableFieldComponent
                        error={errosComerciais.precoHora}
                        value={precoHora}
                        onChange={(val) => {
                            const novoValor = Number(val);


                            if (!usuarioAutorizado &&
                                configOriginal?.precoHora &&
                                novoValor < configOriginal.precoHora
                            ) {
                                showAlertComponent(
                                    "erro",
                                    "You cannot reduce the amount. An approval request has been sent."
                                );

                                solicitarReducaoPreco(novoValor);
                                return;
                            }

                            setConfig(p => ({ ...p, precoHora: novoValor || 0 }));
                        }}
                        currency={config.moeda || 'USD'}
                        locale="en-US"
                    />


                </FieldWithTooltipComponent>
            </div>

            {estimativas.map((est, index) => {
                const res = resultado.find(r => r.id === est.id) || { horas: 0, preco: 0, moeda };
                const temErro = !!errors[index];

                return (
                    <AccordionItemComponent
                        key={`est-${est.id}`}
                        title={`Estimate ${index + 1}${res?.preco ? ` – ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(res.preco)}` : ''}`}
                        defaultOpen={index === estimativas.length - 1}
                        summary={gerarResumoEstimada(est)}
                        className={temErro ? 'has-error' : ''}
                        data-accordion-index={index}
                    >
                        {['iabStd', 'iabRm', 'dvast'].includes(est.formato) && (
                            <div className="formato-alert">
                                {est.formato === 'iabStd' || est.formato === 'iabRm' ? (
                                    <p>⚠️ This format requires the publisher to accept our ad serving TAG.</p>
                                ) : est.formato === 'dvast' ? (
                                    est.dvastSkip ? (
                                        <p>⚠️ To deliver this format, the publisher must accept our VAST 3.0 TAG.</p>
                                    ) : (
                                        <p>⚠️ To deliver this format, the publisher must accept our VAST 2.0 TAG.</p>
                                    )
                                ) : null}
                            </div>
                        )}

                        <TitleSectionComponent>Creative Information</TitleSectionComponent>
                        <div className="input-row">
                            <FieldWithTooltipComponent label="Creative Type" tooltip="Choose the creative type according to IAB standards or project requirements.">
                                <SelectComponent
                                    value={est.formato}
                                    onChange={(e) => handleMultiChange(index, 'formato', e.target.value)}
                                    options={formatoOptions}
                                    error={errors[index]?.formato}

                                    errorMessage={errors[index]?.formato && "Required, please select the creative type."}
                                />
                            </FieldWithTooltipComponent>

                            {est.formato === 'shoppable' && (
                                <FieldWithTooltipComponent label="Has carousel?" tooltip="Define whether the Shoppable creative will have card/carousel rotation.">
                                    <RadioGroupComponent
                                        name={`carrossel-${est.id}`}
                                        options={opcoesSimNao}
                                        value={est.carrossel}
                                        onChange={(val) => handleMultiChange(index, 'carrossel', val === "true")}
                                    />
                                </FieldWithTooltipComponent>
                            )}

                            {est.formato === 'dvast' && (
                                <FieldWithTooltipComponent label="Has Skip?" tooltip="DVAST allows skipping after a few seconds.">
                                    <RadioGroupComponent
                                        name={`dvastSkip-${est.id}`}
                                        options={opcoesSimNao}
                                        value={est.dvastSkip}
                                        onChange={(val) => handleMultiChange(index, 'dvastSkip', val === "true")}
                                    />
                                </FieldWithTooltipComponent>
                            )}

                            <FieldWithTooltipComponent label="Interaction Levels (steps)" tooltip="Number of interactions within the creative.">
                                <SelectComponent
                                    value={est.steps}
                                    onChange={(e) => handleMultiChange(index, 'steps', e.target.value)}
                                    options={stepsOptions}
                                    error={errors[index]?.steps}

                                    errorMessage={errors[index]?.steps && "Required, please select interaction levels."}
                                />
                            </FieldWithTooltipComponent>

                            <FieldWithTooltipComponent label="Versions per format" tooltip="How many content variations will be made for each format.">
                                <InputComponent
                                    type="number"
                                    min={0}
                                    step={1}
                                    value={est.versoes}
                                    onChange={(e) => handleMultiChange(index, 'versoes', Number(e.target.value))}
                                    className={errors[index]?.versoes ? 'error' : ''}
                                    errorMessage={errors[index]?.versoes && "Required, please enter the number of versions per format."}
                                />
                            </FieldWithTooltipComponent>

                            <FieldWithTooltipComponent
                                label="Dynamic Items?"
                                tooltip="Creatives with products, prices, or texts from a feed."
                            >
                                <RadioGroupComponent
                                    name={`itensDinamicos-${est.id}`}
                                    options={opcoesSimNao}
                                    value={est.possuiItensDinamicos}
                                    onChange={(val) => handleMultiChange(index, 'possuiItensDinamicos', val === "true")}
                                    error={errors[index]?.possuiItensDinamicos}
                                />
                                {errors[index]?.possuiItensDinamicos && (
                                    <small className="error-message">{errors[index]?.possuiItensDinamicos}</small>
                                )}

                                {est.possuiItensDinamicos === true && (
                                    <div className="textarea-wrapper">
                                        <TextareaComponent
                                            label="Description"
                                            name="descricaoItensDinamicos"
                                            value={est.descricaoItensDinamicos}
                                            onChange={(e) => handleMultiChange(index, 'descricaoItensDinamicos', e.target.value)}
                                            placeholder="Describe the dynamic items..."
                                            error={errors[index]?.descricaoItensDinamicos}
                                        />
                                        {errors[index]?.descricaoItensDinamicos && (
                                            <small className="error-message">{errors[index]?.descricaoItensDinamicos}</small>
                                        )}
                                    </div>
                                )}

                            </FieldWithTooltipComponent>

                            <FieldWithTooltipComponent
                                label="Dimensions (Optional)"
                                tooltip="Select the desired dimensions or manually enter in 'Others'."
                            >
                                <MultiCheckboxSelectComponent
                                    options={dimensoesOptions.filter(o => o.value)}
                                    selectedValues={est.dimensoesSelecionadas || []}
                                    onChange={(values) => {
                                        if (values.includes("all")) {
                                            handleMultiChange(index, 'dimensoesSelecionadas', []);
                                            return;
                                        }
                                        handleMultiChange(index, 'dimensoesSelecionadas', values);
                                    }}
                                    showSelectComponentAll={true}
                                    selectAllLabel="Select all"
                                />

                                {est.dimensoesSelecionadas?.includes('outros') && (
                                    <>
                                        <TextareaComponent
                                            label="Other dimensions (Optional)"
                                            name="outrasDimensoes"
                                            value={est.outrasDimensoes}
                                            onChange={(e) => handleMultiChange(index, 'outrasDimensoes', e.target.value)}
                                            placeholder="Ex: 970x250, 300x600"
                                        />

                                        <small className="info-message">Separate by commas in the Width x Height format.</small>
                                    </>
                                )}

                            </FieldWithTooltipComponent>
                        </div>

                        <TitleSectionComponent>Technical Configuration</TitleSectionComponent>
                        <div className="input-row">
                            <FieldWithTooltipComponent label="Provided Material" tooltip="Type of creative material provided by the client.">
                                <SelectComponent
                                    value={est.mockup}
                                    onChange={(e) => handleMultiChange(index, 'mockup', e.target.value)}
                                    options={mockupOptions}
                                    error={errors[index]?.mockup}

                                    errorMessage={errors[index]?.mockup && "Required, please specify the available creative material."}
                                />
                            </FieldWithTooltipComponent>

                            {['desdobrar', 'criar', 'clienteFornece'].includes(est.mockup) && (
                                <FieldWithTooltipComponent label={getAdaptacaoLabel(est.mockup)} tooltip="Each material represents a creative format.">
                                    <InputComponent
                                        type="number"
                                        min="1"
                                        step="1"
                                        value={est.desdobramentos}
                                        onChange={(e) => {
                                            const value = Number(e.target.value);
                                            handleMultiChange(index, 'desdobramentos', value >= 1 ? value : 0);
                                        }}
                                        className={errors[index]?.desdobramentos ? 'error' : ''}
                                        errorMessage={errors[index]?.desdobramentos && "Informe quantos formatos (desdobramentos) serão criados."}
                                    />
                                </FieldWithTooltipComponent>
                            )}

                            <FieldWithTooltipComponent label="Feed Method" tooltip="How dynamic data will be fed into the creative.">
                                <SelectComponent
                                    value={est.alimentacao}
                                    onChange={(e) => handleMultiChange(index, 'alimentacao', e.target.value)}
                                    options={alimentacaoOptions}
                                    error={errors[index]?.alimentacao}

                                    errorMessage={errors[index]?.alimentacao && "Required, please select the feed integration method."}
                                />
                            </FieldWithTooltipComponent>

                            {['powerfeed', 'feed'].includes(est.alimentacao) && (
                                <FieldWithTooltipComponent
                                    label={`Price per line (${config.moeda || 'USD'})`}
                                    tooltip="Used to calculate the total cost according to the number of versions"
                                >
                                    <EditableFieldComponent
                                        value={est.precoPorLinhaPowerfeeds || 0}
                                        onChange={(val) => handleMultiChange(index, 'precoPorLinhaPowerfeeds', val)}
                                        currency={config.moeda || 'USD'}
                                        locale="en-US"
                                    />
                                    {est.alimentacao === 'powerfeed' && (
                                        <small className="info-message">
                                            Enter the number of versions to correctly calculate the Powerfeed line cost.
                                        </small>
                                    )}
                                </FieldWithTooltipComponent>
                            )}
                        </div>

                        <TitleSectionComponent>Performance and Deliverables</TitleSectionComponent>
                        <div className="input-row">
                            <FieldWithTooltipComponent label="Volume" tooltip="Estimated impression volume. May influence performance.">
                                <SelectComponent
                                    value={est.volumetria}
                                    onChange={(e) => handleMultiChange(index, 'volumetria', e.target.value)}
                                    options={volumetriaOptions}
                                    error={errors[index]?.volumetria}

                                    errorMessage={errors[index]?.volumetria && "Required, please enter the estimated volume."}
                                />
                            </FieldWithTooltipComponent>

                            <FieldWithTooltipComponent label="Advanced Viewability (Optional)" tooltip="Enable if the creative requires above-average viewability.">
                                <label className="checkbox-item" >
                                    <input
                                        type="checkbox"
                                        checked={est.viewabilityAvancado}
                                        onChange={() => handleMultiChange(index, 'viewabilityAvancado', !est.viewabilityAvancado)}
                                    />
                                    Enable
                                </label>
                            </FieldWithTooltipComponent>

                            <FieldWithTooltipComponent
                                label="Personalization Logic (Optional)"
                                tooltip="Determines the complexity of segmentation and dynamic creative logic"
                            >
                                <MultiCheckboxSelectComponent
                                    options={(tipoDcoOptions || [])
                                        .filter(o => o?.value && o.value !== 'Select')
                                        .map(o => ({ label: o.label, value: o.value }))}
                                    selectedValues={est.tipoDCO || []}
                                    onChange={(values) => handleMultiChange(index, 'tipoDCO', values)}
                                    showSelectComponentAll
                                    selectAllLabel="Select all"
                                />
                            </FieldWithTooltipComponent>
                        </div>

                        <ButtonComponent
                            variant="delete"
                            className="btn-delete"
                            onClick={() => handleRemoveClick(index)}
                            disabled={estimativas.length === 1}
                        >
                            Remove estimate
                        </ButtonComponent>
                    </AccordionItemComponent>
                );
            })}

            <div className="btn-group btn-lista">
                <ButtonComponent
                    variant="add"
                    className="btn-add"
                    onClick={() => {
                        const newId = nextId.current++;
                        setEstimates((prev) => [...prev, NOVA_ESTIMATIVA(newId)]);
                    }}
                >
                    New estimate
                </ButtonComponent>

                {showPopup && (
                    <ConfirmationPopupComponent
                        onClose={() => setUIState(p => ({ ...p, showPopup: false }))}
                        onConfirm={handleConfirmDelete}
                    />
                )}
            </div>

            <div className="btn-group btn-acoes">
                <ButtonComponent
                    className="btn-primary"
                    onClick={() => {
                        calcularEsforco();
                        setPrimeiroCalculo(false);
                        setAlteracoes(0);
                    }}
                >
                    <i className="fa fa-calculator"></i> Calculate estimate
                </ButtonComponent>


                <ButtonComponent
                    type="button"
                    className={`btn-add ${resultado.length === 0 || pendenteAprovacao ? 'btn-disable' : ''}`}
                    disabled={resultado.length === 0 || pendenteAprovacao}
                    onClick={() => !pendenteAprovacao && handleGerarPDF('comercial')}
                    title={pendenteAprovacao ? "PDF locked until approval." : ""}
                >
                    <i className="fa fa-download"></i> PDF Report
                </ButtonComponent>

                {/*  Send for Approval → apenas NÃO autorizado */}
                {!usuarioAutorizado && (
                    <ButtonComponent
                        type="button"
                        variant="approve"
                        className={`btn-approve ${resultado.length === 0 ? 'btn-disable' : ''}`}
                        disabled={
                            resultado.length === 0 ||
                            (priceReduced && pendenteAprovacao)
                        }


                        onClick={() => handleGerarPDF('aprovacao')}
                    >
                        <i className="fa fa-paper-plane"></i> Send for Approval
                    </ButtonComponent>
                )}


                <ButtonComponent
                    variant="reset"
                    disabled={!isLimparAtivo}
                    onClick={() => {
                        nextId.current = 2;
                        setEstimates([NOVA_ESTIMATIVA(1)]);
                        setResultado([]);
                        setDadosComerciais({
                            ...dadosComerciais,
                            nomeProjeto: '',
                            nomeCliente: '',
                            urgencia: ''
                        });
                        setAlteracoes(0);
                        setPrimeiroCalculo(true);
                        setPendenteAprovacao(false);
                    }}
                >
                    <i className="fa fa-redo"></i> Clear estimates
                </ButtonComponent>
            </div>


            {pendenteAprovacao && (
                <div className="approval-status">
                    <TitleSectionComponent>Approval Status</TitleSectionComponent>

                    <div className="status-card pending">
                        {/* ✅ Badge visual */}
                        <span className="badge badge-pending">
                            <i className="fa fa-clock"></i> Pending Approval
                        </span>

                        <div className="details">
                            <span>Waiting for approval from Marcelo / Raul</span>

                            {/* ✅ Motivo da pendência */}
                            {pendingReason && (
                                <small className="reason">
                                    Reason: {pendingReason}
                                </small>
                            )}

                            <small>
                                Sent on: {new Date().toLocaleString()}
                            </small>
                        </div>
                    </div>

                    {/* ✅ Permitir só download de cópia, nunca o original */}
                    <ButtonComponent
                        variant="secondary"
                        className="btn-copy-download"
                        title="This is a non-final copy. The official version is locked until approval."
                        onClick={() => handleGerarPDF('comercial')}
                    >
                        <i className="fa fa-download"></i> Download Copy
                    </ButtonComponent>
                </div>
            )}


            {loading && (
                <div className="loading-indicator" style={{ margin: "10px 0" }}>
                    <i className="fa fa-spinner fa-spin"></i> Updating...
                </div>
            )}


            {resultado.length > 0 && (
                <div ref={resultadoRef} className={highlightResultado ? 'resultado-highlight' : ''}>
                    <TitleSectionComponent>Estimate Details:</TitleSectionComponent>
                    <table className="result-table summary-table">
                        <thead>
                            <tr>
                                <th>Id</th>
                                <th>Format</th>
                                <th>Estimated Hours</th>
                                <th>Estimated Price ({moeda || 'USD'})</th>
                            </tr>
                        </thead>
                        <tbody>
                            {resultado.map((res, index) => (
                                <tr key={index}>
                                    <td>{res.id}</td>
                                    <td>{formatoOptions.find((opt) => opt.value === res.formato)?.label || res.formato}</td>
                                    <td>{formatHours(res.horas)}</td>
                                    <td>{formatCurrency(res.preco, config.moeda)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {resultado.length > 1 && (
                <div className="tabela-final-resumo">
                    <TitleSectionComponent>Consolidated Totals of Estimates:</TitleSectionComponent>
                    <table className="result-table">
                        <thead>
                            <tr>
                                <th>Total Time</th>
                                <th>Total Price</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    {formatHours(
                                        resultado.reduce((acc, r) => acc + (r.horas || 0), 0)
                                    )}
                                </td>
                                <td>
                                    {formatCurrency(
                                        resultado.reduce((acc, r) => acc + (r.preco || 0), 0),
                                        config.moeda
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};

export default DCOEstimator;